import type { ActivityDTO } from 'src/types/dto/activity.dto';
import type { AuthorizationDTO } from 'src/types/dto/authorization.dto';
import type { InspectorDTO } from 'src/types/dto/inspector.dto';
import type { RescheduleSuggestionDTO } from 'src/types/dto/reschedule-suggestion.dto';

import axios, { endpoints } from 'src/utils/axios';

import { getActivityCurrentAppointments } from './utils';

const needsActivityFetch = (activity?: ActivityDTO) =>
  Boolean(activity?.id && activity.status === undefined);

const needsInspectorFetch = (inspector?: InspectorDTO) =>
  Boolean(inspector?.id && !inspector.name);

const needsInspectorAuthorizationsFetch = (inspector?: InspectorDTO) =>
  Boolean(inspector?.id && !inspector.authorizations?.length);

const collectInspectorIdsFromActivity = (
  activity: ActivityDTO | undefined,
  inspectorIds: Set<number>
) => {
  getActivityCurrentAppointments(activity).forEach((appointment) => {
    if (needsInspectorFetch(appointment.inspector)) {
      inspectorIds.add(appointment.inspector.id);
    } else if (needsInspectorAuthorizationsFetch(appointment.inspector)) {
      inspectorIds.add(appointment.inspector.id);
    }
  });
};

export const fetchActivity = async (activityId: number) => {
  const { data } = await axios.get<ActivityDTO>(
    endpoints.activities.details(activityId.toString())
  );
  return data;
};

export const fetchInspectorWithAuthorizations = async (
  inspectorId: number
): Promise<InspectorDTO> => {
  const [inspectorResponse, authorizationsResponse] = await Promise.all([
    axios.get<InspectorDTO>(endpoints.inspectors.getInspector(inspectorId)),
    axios.get<{ authorizations: AuthorizationDTO[] }>(endpoints.authorizations.list, {
      params: { 'inspector-id': inspectorId },
    }),
  ]);

  return {
    ...inspectorResponse.data,
    authorizations: authorizationsResponse.data.authorizations ?? [],
  };
};

export const enrichRescheduleSuggestion = async (
  suggestion: RescheduleSuggestionDTO,
  currentActivity: ActivityDTO
): Promise<RescheduleSuggestionDTO> => {
  const activityIds = new Set<number>();
  const inspectorIds = new Set<number>();

  if (needsInspectorFetch(suggestion.inspector)) {
    inspectorIds.add(suggestion.inspector.id);
  } else if (needsInspectorAuthorizationsFetch(suggestion.inspector)) {
    inspectorIds.add(suggestion.inspector.id);
  }

  suggestion.changes.forEach((change) => {
    const activityId = change.activity?.id;
    if (activityId && activityId !== currentActivity.id && needsActivityFetch(change.activity)) {
      activityIds.add(activityId);
    }
  });

  const activityMap = new Map<number, ActivityDTO>();

  const resolveActivity = (activity: ActivityDTO) => {
    if (activity.id === currentActivity.id) {
      return currentActivity;
    }

    return activityMap.get(activity.id) ?? activity;
  };

  await Promise.all([
    ...Array.from(activityIds).map(async (activityId) => {
      activityMap.set(activityId, await fetchActivity(activityId));
    }),
  ]);

  suggestion.changes.forEach((change) => {
    collectInspectorIdsFromActivity(resolveActivity(change.activity), inspectorIds);
  });

  const inspectorMap = new Map<number, InspectorDTO>();

  await Promise.all([
    ...Array.from(inspectorIds).map(async (inspectorId) => {
      inspectorMap.set(inspectorId, await fetchInspectorWithAuthorizations(inspectorId));
    }),
  ]);

  const resolveInspector = (inspector?: InspectorDTO) => {
    if (!inspector?.id) {
      return inspector;
    }

    return inspectorMap.get(inspector.id) ?? inspector;
  };

  const enrichActivity = (activity: ActivityDTO): ActivityDTO => ({
    ...activity,
    appointments: getActivityCurrentAppointments(activity).map((appointment) => {
      const inspector = appointment.inspector
        ? resolveInspector(appointment.inspector) ?? appointment.inspector
        : appointment.inspector;

      return inspector ? { ...appointment, inspector } : appointment;
    }),
  });

  return {
    ...suggestion,
    inspector: resolveInspector(suggestion.inspector) ?? suggestion.inspector,
    changes: suggestion.changes.map((change) => ({
      ...change,
      activity: enrichActivity(resolveActivity(change.activity)),
    })),
  };
};
